import React, { useState } from 'react';
import { FaCloudUploadAlt, FaFilePdf, FaBook, FaTrash, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import axios from 'axios';
// apiEndpoint joins REACT_APP_APIURL and the path safely, and throws (shown as
// an upload error) if REACT_APP_APIURL isn't set, instead of silently posting
// to the panel's own origin
import { apiEndpoint } from '../utils/apiUrl';

const ALLOWED_FORMATS = ['pdf', 'epub'];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const EbookFileUpload = ({ businessId, onUploadComplete, existingFile = null }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [uploadedFileData, setUploadedFileData] = useState(existingFile);
  const [dragActive, setDragActive] = useState(false);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const validateFile = (file) => {
    const extension = file.name.split('.').pop().toLowerCase();
    
    if (!ALLOWED_FORMATS.includes(extension)) {
      return `Invalid file format. Allowed: ${ALLOWED_FORMATS.join(', ').toUpperCase()}`;
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size: ${formatFileSize(MAX_FILE_SIZE)}`;
    }
    
    return null;
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;

    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }

    setFile(selectedFile);
    setError('');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setProgress(0);
    setError('');

    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('business_id', businessId);

      // Upload file via backend (no CORS issues)
      const uploadResponse = await axios.post(apiEndpoint('digital/upload'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
        }
      });

      const fileData = uploadResponse.data.result;

      setUploadedFileData(fileData);
      onUploadComplete(fileData);
      setFile(null);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setFile(null);
    setUploadedFileData(null);
    setError('');
    onUploadComplete(null);
  };

  const getFileIcon = (format) => {
    switch (format) {
      case 'pdf':
        return <FaFilePdf style={{ fontSize: '40px', color: '#dc3545' }} />;
      case 'epub':
        return <FaBook style={{ fontSize: '40px', color: '#1E5B3A' }} />;
      default:
        return <FaCloudUploadAlt style={{ fontSize: '40px', color: '#5B5040' }} />;
    }
  };

  return (
    <div>
      {!uploadedFileData ? (
        <>
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('ebook-file-input').click()}
            className="border border-dashed rounded p-4 text-center"
            style={{
              borderColor: dragActive ? '#2E7D4F' : '#dee2e6',
              backgroundColor: dragActive ? '#e7ffe7' : '#FDF9F2',
              cursor: uploading ? 'not-allowed' : 'pointer',
              opacity: uploading ? 0.6 : 1,
              transition: 'all 0.3s'
            }}
          >
            <input
              id="ebook-file-input"
              type="file"
              accept=".pdf,.epub"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files[0])}
              disabled={uploading}
            />

            <FaCloudUploadAlt style={{ fontSize: '48px', color: '#5B5040', margin: '0 auto 15px' }} />
            <p className="mb-2" style={{ fontSize: '16px', fontWeight: '500' }}>
              Click to select or drag & drop your e-book file
            </p>
            <p className="text-muted" style={{ fontSize: '14px' }}>
              Supported formats: PDF, EPUB (Max: {formatFileSize(MAX_FILE_SIZE)})
            </p>
          </div>

          {file && (
            <div className="card mt-3">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  {getFileIcon(file.name.split('.').pop().toLowerCase())}
                  <div className="flex-fill ml-3">
                    <p className="mb-1" style={{ fontWeight: '600' }}>{file.name}</p>
                    <p className="text-muted mb-0" style={{ fontSize: '12px' }}>{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    className="btn btn-sm btn-outline-danger"
                  >
                    <FaTrash />
                  </button>
                </div>

                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="btn btn-success btn-block"
                >
                  <FaCloudUploadAlt className="mr-2" />
                  {uploading ? 'Uploading...' : 'Upload File'}
                </button>

                {uploading && (
                  <div className="mt-3">
                    <div className="progress" style={{ height: '8px' }}>
                      <div
                        className="progress-bar bg-success"
                        role="progressbar"
                        style={{ width: `${progress}%` }}
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                    <p className="text-center text-muted mt-2" style={{ fontSize: '12px' }}>
                      {progress}% uploaded
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="card border-success">
          <div className="card-body" style={{ backgroundColor: '#d4edda' }}>
            <div className="d-flex align-items-center">
              <FaCheckCircle style={{ fontSize: '40px', color: '#2E7D4F' }} />
              <div className="flex-fill ml-3">
                <p className="mb-1" style={{ fontWeight: '600' }}>{uploadedFileData.file_name}</p>
                <div className="d-flex align-items-center">
                  <span className="badge badge-success mr-2">
                    {uploadedFileData.file_format.toUpperCase()}
                  </span>
                  <span className="text-muted" style={{ fontSize: '12px' }}>
                    {formatFileSize(uploadedFileData.file_size)}
                  </span>
                </div>
              </div>
              <button
                onClick={handleRemove}
                className="btn btn-sm btn-outline-danger"
              >
                Replace
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger mt-3 d-flex align-items-start" role="alert">
          <FaExclamationCircle className="mr-2 mt-1" />
          <div>{error}</div>
        </div>
      )}
    </div>
  );
};

export default EbookFileUpload;
